import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import {
  BoqBomQuestions,
  BoqBomQuestionsFooter,
  MOCK_AI_PRESUMED_VALUES
} from '../../components/boq-bom/BoqBomQuestions';
import {
  BoqBomDetailChrome,
  BOQ_BOM_DETAIL_CONTENT_OFFSET
} from '../../components/boq-bom/BoqBomScreenHeader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import Loader from '../../../shared/components/ui/Loader';
import {
  generateBoq,
  getBoqQuestions,
  updateBoqOption,
  updateBoqQuestion
} from '../../services/api/implementation/impl';
import {
  mapBoqGenerateResponse,
  mapBoqQuestionUpdateResponse,
  mapBoqQuestionsResponse,
  getApiErrorMessage,
  isGroupCommonExtractRequiredError
} from '../../utils/boqBomExtract';
import {
  buildBoqQuestionsOptions,
  buildGenerateBoqOptions,
  getBoqBomAccommodationId,
  getBoqBomBasePath,
  getBoqBomBreadcrumbFlowLabel,
  getBoqBomDisplayName,
  getBoqBomWorkTypeIds,
  resolveBoqBomFlow,
  resolveBoqBomFlowApiOptions
} from '../../utils/boqBomRoutes';

const BOQ_BOM_PROJECT_STORAGE_KEY = 'boqBomSelectedProjectId';

const BoqBomQuestionsPage = () => {
  const { collapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const queryClient = useQueryClient();
  const { projects } = useProjectsList();

  const decodedName = useMemo(
    () => getBoqBomDisplayName(params, location.state),
    [params, location.state]
  );

  const accommodationId = useMemo(
    () => getBoqBomAccommodationId(params, location.state),
    [params, location.state]
  );

  const breadcrumbFlowLabel = useMemo(
    () => getBoqBomBreadcrumbFlowLabel(location.pathname),
    [location.pathname]
  );

  const flowApiOptions = useMemo(
    () => resolveBoqBomFlowApiOptions(location.pathname, location.state),
    [location.pathname, location.state]
  );

  const isCommonAreaFlow = resolveBoqBomFlow(location.pathname) === 'common-area';

  const projectId = useMemo(() => {
    if (location.state?.projectId != null) return Number(location.state.projectId);
    const savedId = localStorage.getItem(BOQ_BOM_PROJECT_STORAGE_KEY);
    return savedId ? parseInt(savedId, 10) : null;
  }, [location.state]);

  const workTypeIds = useMemo(() => getBoqBomWorkTypeIds(location.state), [location.state]);

  const analysisData = location.state?.analysisData;
  const documentType = location.state?.documentType ?? 'Clarification questions';
  const topicLabel = location.state?.topicLabel ?? documentType;

  const questionsQueryKey = useMemo(
    () => [
      'boqQuestions',
      isCommonAreaFlow ? 'project' : 'accommodation',
      isCommonAreaFlow ? projectId : accommodationId,
      flowApiOptions.intelligenceScope
    ],
    [isCommonAreaFlow, projectId, accommodationId, flowApiOptions.intelligenceScope]
  );

  const questionsQueryEnabled = isCommonAreaFlow ? Boolean(projectId) : Boolean(accommodationId);

  const {
    data: questionsData,
    isLoading: isQuestionsLoading,
    isError: isQuestionsError,
    error: questionsError
  } = useQuery({
    queryKey: questionsQueryKey,
    queryFn: () =>
      getBoqQuestions(
        isCommonAreaFlow ? null : accommodationId,
        buildBoqQuestionsOptions(flowApiOptions, isCommonAreaFlow ? { projectId } : {})
      ),
    enabled: questionsQueryEnabled,
    select: mapBoqQuestionsResponse
  });

  const canAnswerQuestions = isCommonAreaFlow
    ? Boolean(projectId)
    : Boolean(accommodationId);

  const readyForBoqEngine = Boolean(questionsData?.readyForBoqEngine);
  const notesForNextStep = questionsData?.notesForNextStep;
  const serverAnsweredCount = questionsData?.answeredCount ?? 0;
  const serverTotalQuestions = questionsData?.totalQuestions ?? 0;
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateExtractError, setGenerateExtractError] = useState(null);

  const extractRequiredError = useMemo(() => {
    if (generateExtractError) return generateExtractError;
    if (questionsError && isGroupCommonExtractRequiredError(questionsError)) {
      return getApiErrorMessage(questionsError);
    }
    return null;
  }, [generateExtractError, questionsError]);

  useEffect(() => {
    if (isQuestionsError && !extractRequiredError) {
      toast.error('Failed to load clarification questions');
    }
  }, [isQuestionsError, extractRequiredError]);

  const questionSections = useMemo(() => {
    // Only render persisted server questions for answering. Analysis-step sections
    // can include non-persisted options (no DB ids) and a different count than GET /boq/questions.
    if (questionsData) return questionsData.questionSections ?? [];
    return [];
  }, [questionsData]);

  const totalQuestions = useMemo(() => {
    if (questionsData?.totalQuestions != null) return questionsData.totalQuestions;
    return questionSections.reduce((sum, section) => sum + (section.questions?.length ?? 0), 0);
  }, [questionsData?.totalQuestions, questionSections]);

  const presumedValues = analysisData?.presumedValues?.length
    ? analysisData.presumedValues
    : MOCK_AI_PRESUMED_VALUES;
  const presumedCount = analysisData?.presumedCount ?? presumedValues.length;

  const projectName = useMemo(() => {
    if (location.state?.projectName) return location.state.projectName;
    const savedId = localStorage.getItem(BOQ_BOM_PROJECT_STORAGE_KEY);
    const projectId =
      location.state?.projectId ?? (savedId ? parseInt(savedId, 10) : null);
    if (!projectId || !projects?.length) return 'Project';
    return projects.find((p) => p.id === projectId)?.name ?? 'Project';
  }, [location.state, projects]);

  const detailPath = useMemo(
    () => getBoqBomBasePath(location.pathname, decodedName, params),
    [location.pathname, decodedName, params]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const buildDetailState = (extra = {}) => ({
    ...location.state,
    categoryName: decodedName,
    analysisData,
    analysisComplete: true,
    accommodationId,
    projectId,
    flowApiOptions,
    workTypes: location.state?.workTypes,
    generatedBoqData: location.state?.generatedBoqData,
    generatedBomData: location.state?.generatedBomData,
    boqGenerated: location.state?.boqGenerated,
    bomGenerated: location.state?.bomGenerated,
    ...extra
  });

  const handleBack = () => {
    navigate(detailPath, { state: buildDetailState({ returnTab: 'BOQ' }) });
  };

  const handlePreviewBoq = () => {
    navigate(detailPath, { state: buildDetailState({ returnTab: 'BOQ' }) });
  };

  const handleGoToUpload = () => {
    const {
      analysisComplete: _analysisComplete,
      analysisData: _analysisData,
      boqGenerated: _boqGenerated,
      bomGenerated: _bomGenerated,
      generatedBoqData: _generatedBoqData,
      generatedBomData: _generatedBomData,
      ...restState
    } = location.state || {};

    navigate(detailPath, {
      state: {
        ...restState,
        categoryName: decodedName,
        accommodationId,
        flowApiOptions,
        returnTab: 'BOQ',
        startUpload: true
      }
    });
  };

  const handleGenerateBoq = async () => {
    if (!readyForBoqEngine) {
      toast.error(
        notesForNextStep ?? 'Complete all clarification questions before generating BOQ.'
      );
      return;
    }
    if (isCommonAreaFlow) {
      if (!projectId) {
        toast.error('Project is required to generate BOQ.');
        return;
      }
      if (!workTypeIds.length) {
        toast.error('Common area work types are required to generate BOQ.');
        return;
      }
    } else if (!accommodationId) {
      toast.error('Accommodation is required to generate BOQ.');
      return;
    }

    setIsGenerating(true);
    try {
      const generateOptions = isCommonAreaFlow
        ? buildGenerateBoqOptions(flowApiOptions, {
            projectId,
            workTypeIds,
            workTypes: location.state?.workTypes
          })
        : buildGenerateBoqOptions(flowApiOptions);

      const response = await generateBoq(
        isCommonAreaFlow ? null : accommodationId,
        generateOptions
      );
      const generatedBoqData = mapBoqGenerateResponse(response);
      toast.success('BOQ generated successfully');
      navigate(detailPath, {
        state: buildDetailState({
          returnTab: 'BOQ',
          boqGenerated: true,
          generatedBoqData
        })
      });
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to generate BOQ');
      if (isGroupCommonExtractRequiredError(err)) {
        setGenerateExtractError(message);
      }
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const [progress, setProgress] = useState({
    answeredCount: 0,
    totalQuestions
  });

  useEffect(() => {
    setProgress((prev) => ({ ...prev, totalQuestions }));
  }, [totalQuestions]);

  const handleProgressChange = useCallback((next) => {
    setProgress(next);
  }, []);

  const refetchQuestions = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: questionsQueryKey });
    await queryClient.refetchQueries({ queryKey: questionsQueryKey });
  }, [questionsQueryKey, queryClient]);

  const handleSelectOption = useCallback(
    async (questionId, optionId, answerLabel) => {
      try {
        const numericOptionId = Number(optionId);
        // Preset option rows are best-effort; question.answer is what unlocks BOQ.
        if (Number.isFinite(numericOptionId) && numericOptionId > 0) {
          try {
            await updateBoqOption(numericOptionId, { answer: answerLabel });
          } catch (optErr) {
            console.warn('updateBoqOption failed; saving question answer only', optErr);
          }
        }

        const questionResponse = await updateBoqQuestion(questionId, {
          answer: answerLabel
        });
        const updated = mapBoqQuestionUpdateResponse(questionResponse);

        await refetchQuestions();
        toast.success('Answer saved');
        return updated;
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to save answer');
        throw err;
      }
    },
    [refetchQuestions]
  );

  const handleSubmitCustomAnswer = useCallback(
    async (questionId, answer) => {
      try {
        const response = await updateBoqQuestion(questionId, { answer });
        const updated = mapBoqQuestionUpdateResponse(response);
        await queryClient.invalidateQueries({ queryKey: questionsQueryKey });
        toast.success('Answer saved');
        return updated;
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to save answer');
        throw err;
      }
    },
    [questionsQueryKey, queryClient]
  );

  const hasQuestions = questionSections.some((section) => section.questions?.length > 0);
  const showQuestions = !isQuestionsLoading && hasQuestions;

  return (
    <Layout>
      <BoqBomDetailChrome
        collapsed={collapsed}
        onBack={handleBack}
        projectName={projectName}
        flowLabel={breadcrumbFlowLabel}
        pageHeaderProps={{
          breadcrumbLabel: decodedName,
          title: `${decodedName} · Questions`,
          subtitle: `Answer questions for missing document types · ${topicLabel}`
        }}
      />

      <div
        className={`min-h-screen bg-[#F4F6F7] ${BOQ_BOM_DETAIL_CONTENT_OFFSET.withoutTabs} pb-28`}>
        <div className="px-6 md:px-8 pt-6">
          {!questionsQueryEnabled && (
            <p className="text-[14px] text-[#6B7280] mb-4">
              {isCommonAreaFlow
                ? 'Project is required to load clarification questions.'
                : 'Accommodation ID is required to load clarification questions.'}
            </p>
          )}

          {extractRequiredError && (
            <div className="mx-auto max-w-[720px] rounded-xl border border-[#FCD34D] bg-[#FFFBEB] px-6 py-8 text-center">
              <p className="text-[14px] text-[#92400E] leading-relaxed">{extractRequiredError}</p>
              <button
                type="button"
                onClick={handleGoToUpload}
                className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-[#2A6A73] px-5 text-[14px] font-medium text-white hover:bg-[#245a62]">
                Upload files
              </button>
            </div>
          )}

          {isQuestionsLoading && !extractRequiredError && (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          )}

          {showQuestions && !extractRequiredError && (
            <BoqBomQuestions
              sections={questionSections}
              documentType={documentType}
              totalQuestions={totalQuestions}
              presumedValues={presumedValues}
              presumedCount={presumedCount}
              onSelectOption={canAnswerQuestions ? handleSelectOption : undefined}
              onSubmitCustomAnswer={canAnswerQuestions ? handleSubmitCustomAnswer : undefined}
              onProgressChange={handleProgressChange}
            />
          )}

          {!isQuestionsLoading && questionsQueryEnabled && !hasQuestions && !extractRequiredError && (
            <p className="text-[14px] text-[#6B7280]">No clarification questions found.</p>
          )}
        </div>
      </div>

      <BoqBomQuestionsFooter
        collapsed={collapsed}
        answeredCount={
          questionsData != null ? serverAnsweredCount : progress.answeredCount
        }
        totalQuestions={
          questionsData != null ? serverTotalQuestions : progress.totalQuestions
        }
        readyForBoqEngine={readyForBoqEngine}
        notesForNextStep={notesForNextStep}
        isGenerating={isGenerating}
        onPreviewBoq={handlePreviewBoq}
        onGenerateBoq={handleGenerateBoq}
        extractRequiredError={extractRequiredError}
        onUploadFiles={handleGoToUpload}
      />
    </Layout>
  );
};

export default BoqBomQuestionsPage;
